import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Save, Waves, AlertTriangle, Check } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, Legend } from "recharts";

const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const cell = "rounded-lg border px-2 py-1.5 text-sm outline-none w-full text-right tabular focus:border-[#45423C]";
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function PoolLog() {
  const qc = useQueryClient();
  const [date, setDate] = useState(todayISO());
  const [draft, setDraft] = useState({});
  const [viewPool, setViewPool] = useState("");
  const [days, setDays] = useState(30);

  const { data: meta } = useQuery({ queryKey: ["pools-meta"], queryFn: async () => (await api.get("/pools/meta")).data });
  const { data: day } = useQuery({ queryKey: ["pools-day", date], queryFn: async () => (await api.get(`/pools/day?date=${date}`)).data });

  const pools = meta?.pools || [];
  const ranges = meta?.ranges || {};
  useEffect(() => { if (pools.length && !viewPool) setViewPool(pools[0].id); }, [pools, viewPool]);
  useEffect(() => {
    if (!day) return;
    const d = {};
    for (const r of day.rows) d[r.pool_id] = { ph: r.ph ?? "", chlorine: r.chlorine ?? "", temp: r.temp ?? "", notes: r.notes ?? "", updated_at: r.updated_at };
    setDraft(d);
  }, [day]);

  const { data: series } = useQuery({ queryKey: ["pools-series", viewPool, days], enabled: !!viewPool, queryFn: async () => (await api.get(`/pools/series?pool_id=${viewPool}&days=${days}`)).data });
  const { data: hist } = useQuery({ queryKey: ["pools-history", viewPool], queryFn: async () => (await api.get(`/pools/history?days=10${viewPool ? `&pool_id=${viewPool}` : ""}`)).data });

  const set = (pid, key, val) => setDraft((p) => ({ ...p, [pid]: { ...(p[pid] || {}), [key]: val } }));
  const oob = (key, val) => {
    const rg = ranges[key];
    if (!rg || !rg.flag || val === "" || val === null || val === undefined) return false;
    const n = Number(val); if (Number.isNaN(n)) return false;
    return n < rg.min || n > rg.max;
  };
  const cls = (key, val) => `${cell} ${oob(key, val) ? "border-[#991B1B] bg-[#FEF2F2] text-[#991B1B] font-semibold" : "border-[#E3DED4]"}`;
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const alerts = useMemo(() => {
    let n = 0;
    for (const p of pools) { const d = draft[p.id] || {}; if (oob("ph", d.ph)) n++; if (oob("chlorine", d.chlorine)) n++; }
    return n;
  }, [draft, pools, ranges]);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["pools-day"] }); qc.invalidateQueries({ queryKey: ["pools-history"] }); qc.invalidateQueries({ queryKey: ["pools-series"] }); };

  const saveOne = useMutation({
    mutationFn: async (pid) => { const d = draft[pid] || {}; return (await api.put("/pools/reading", { date, pool_id: pid, ph: num(d.ph), chlorine: num(d.chlorine), temp: num(d.temp), notes: d.notes || "" })).data; },
    onSuccess: (r) => { setDraft((p) => ({ ...p, [r.pool_id]: { ...(p[r.pool_id] || {}), updated_at: r.updated_at } })); invalidate(); toast.success("Αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const saveAll = useMutation({
    mutationFn: async () => { const rows = pools.map((p) => { const d = draft[p.id] || {}; return { pool_id: p.id, ph: num(d.ph), chlorine: num(d.chlorine), temp: num(d.temp), notes: d.notes || "" }; }); return (await api.put("/pools/day", { date, rows })).data; },
    onSuccess: () => { invalidate(); toast.success("Όλες οι μετρήσεις αποθηκεύτηκαν"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const hint = (k) => { const r = ranges[k]; return r ? `${r.min}–${r.max}${r.unit ? " " + r.unit : ""}` : ""; };
  const phR = ranges.ph || {}; const clR = ranges.chlorine || {};
  const chartData = (series?.points || []).map((p) => ({ date: p.date.slice(5), ph: p.ph, chlorine: p.chlorine }));

  return (
    <div className="space-y-6" data-testid="pool-log-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#2E6F7A]">Συντήρηση · Πισίνες</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Μετρήσεις Πισινών</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Καθημερινές μετρήσεις pH, χλωρίου και θερμοκρασίας — αποθήκευση ανά πισίνα.</p>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="pool-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="rounded-xl border border-[#E3DED4] px-3 py-2.5 text-sm outline-none focus:border-[#45423C]" />
          <button data-testid="pool-save-all" onClick={() => saveAll.mutate()} disabled={!day || saveAll.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
            <Save className="h-4 w-4" /> Αποθήκευση όλων
          </button>
        </div>
      </div>

      {alerts > 0 && (
        <div className="flex items-center gap-2 rounded-xl bg-[#FEF2F2] border border-[#F3D2D2] px-4 py-2.5 text-sm text-[#991B1B]" data-testid="pool-alerts">
          <AlertTriangle className="h-4 w-4" /> {alerts} τιμές εκτός ασφαλούς ορίου (pH {hint("ph")} · Χλώριο {hint("chlorine")}).
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="bg-[#F7F5F1] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3 font-semibold">Πισίνα</th>
                <th className="px-3 py-3 font-semibold text-right">pH <span className="text-[#B9AE9A]">({hint("ph")})</span></th>
                <th className="px-3 py-3 font-semibold text-right">Χλώριο <span className="text-[#B9AE9A]">({hint("chlorine")})</span></th>
                <th className="px-3 py-3 font-semibold text-right">Θερμ. <span className="text-[#B9AE9A]">°C</span></th>
                <th className="px-3 py-3 font-semibold">Σημειώσεις</th>
                <th className="px-3 py-3 font-semibold text-center">Αποθήκευση</th>
              </tr>
            </thead>
            <tbody>
              {pools.map((p) => {
                const d = draft[p.id] || {};
                return (
                  <tr key={p.id} className="border-t border-[#F1EDE6]" data-testid={`pool-row-${p.id}`}>
                    <td className="px-4 py-2.5 font-medium text-[#45423C] whitespace-nowrap"><span className="inline-flex items-center gap-2"><Waves className="h-4 w-4 text-[#2E6F7A]" /> {p.name}</span></td>
                    <td className="px-3 py-2 w-24"><input data-testid={`pool-ph-${p.id}`} type="number" step="0.1" value={d.ph ?? ""} onChange={(e) => set(p.id, "ph", e.target.value)} className={cls("ph", d.ph)} /></td>
                    <td className="px-3 py-2 w-24"><input data-testid={`pool-chlorine-${p.id}`} type="number" step="0.1" value={d.chlorine ?? ""} onChange={(e) => set(p.id, "chlorine", e.target.value)} className={cls("chlorine", d.chlorine)} /></td>
                    <td className="px-3 py-2 w-24"><input data-testid={`pool-temp-${p.id}`} type="number" step="0.1" value={d.temp ?? ""} onChange={(e) => set(p.id, "temp", e.target.value)} className={cls("temp", d.temp)} /></td>
                    <td className="px-3 py-2"><input data-testid={`pool-notes-${p.id}`} value={d.notes ?? ""} onChange={(e) => set(p.id, "notes", e.target.value)} placeholder="—" className="rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm outline-none w-full focus:border-[#45423C]" /></td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <button data-testid={`pool-save-${p.id}`} onClick={() => saveOne.mutate(p.id)} disabled={saveOne.isPending}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#2E6F7A] text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90 disabled:opacity-40">
                        <Check className="h-3.5 w-3.5" /> Save
                      </button>
                      {d.updated_at && <div className="text-[10px] text-[#8A8577] mt-1" data-testid={`pool-time-${p.id}`}>{fmtTime(d.updated_at)}</div>}
                    </td>
                  </tr>
                );
              })}
              {pools.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="pool-trend">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center gap-3">
          <span className="font-medium text-[#45423C]">Τάση & Ιστορικό</span>
          <div className="ml-auto flex items-center gap-2">
            <select data-testid="pool-view-select" value={viewPool} onChange={(e) => setViewPool(e.target.value)} className="rounded-lg border border-[#E3DED4] px-3 py-1.5 text-sm outline-none focus:border-[#45423C]">
              {pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select data-testid="pool-view-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-lg border border-[#E3DED4] px-3 py-1.5 text-sm outline-none focus:border-[#45423C]">
              <option value={14}>14 ημέρες</option><option value={30}>30 ημέρες</option><option value={60}>60 ημέρες</option>
            </select>
          </div>
        </div>
        <div className="p-4">
          {chartData.length === 0 ? (
            <div className="py-10 text-center text-sm text-[#6F6A60]">Δεν υπάρχουν αρκετές μετρήσεις για γράφημα ακόμη.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE8DF" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8A8577" }} />
                <YAxis yAxisId="ph" domain={[6.5, 8.5]} tick={{ fontSize: 11, fill: "#2E6F7A" }} width={34} />
                <YAxis yAxisId="cl" orientation="right" domain={[0, 5]} tick={{ fontSize: 11, fill: "#C86235" }} width={30} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #E3DED4" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {phR.min != null && <ReferenceArea yAxisId="ph" y1={phR.min} y2={phR.max} fill="#2E6F7A" fillOpacity={0.06} />}
                <Line yAxisId="ph" type="monotone" dataKey="ph" name="pH" stroke="#2E6F7A" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                <Line yAxisId="cl" type="monotone" dataKey="chlorine" name="Χλώριο (ppm)" stroke="#C86235" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="overflow-x-auto border-t border-[#E3DED4]" data-testid="pool-history">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-[#F7F5F1] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-2.5 font-semibold">Ημ/νία</th><th className="px-3 py-2.5 font-semibold">Πισίνα</th>
                <th className="px-3 py-2.5 font-semibold text-right">pH</th><th className="px-3 py-2.5 font-semibold text-right">Χλώριο</th>
                <th className="px-3 py-2.5 font-semibold text-right">Θερμ.</th><th className="px-3 py-2.5 font-semibold">Ώρα</th><th className="px-3 py-2.5 font-semibold">Ποιος</th>
              </tr>
            </thead>
            <tbody>
              {(hist?.rows || []).map((x) => (
                <tr key={`${x.date}-${x.pool_id}`} className="border-t border-[#F1EDE6]">
                  <td className="px-4 py-2 tabular text-[#6F6A60]">{x.date}</td>
                  <td className="px-3 py-2 text-[#45423C]">{x.name}</td>
                  <td className={`px-3 py-2 text-right tabular ${oob("ph", x.ph) ? "text-[#991B1B] font-semibold" : ""}`}>{x.ph ?? "–"}</td>
                  <td className={`px-3 py-2 text-right tabular ${oob("chlorine", x.chlorine) ? "text-[#991B1B] font-semibold" : ""}`}>{x.chlorine ?? "–"}</td>
                  <td className="px-3 py-2 text-right tabular text-[#6F6A60]">{x.temp ?? "–"}</td>
                  <td className="px-3 py-2 tabular text-xs text-[#8A8577]">{x.updated_at ? fmtTime(x.updated_at) : "—"}</td>
                  <td className="px-3 py-2 text-[#6F6A60] text-xs">{x.recorded_by || "—"}</td>
                </tr>
              ))}
              {(hist?.rows || []).length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6F6A60]">Καμία μέτρηση ακόμη.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
